import { HymnalSlug, Org, OrganizationKey, OrgId, SongNumber, SongSlug } from './model.ts';

export const musicManifestFilename = 'manifest.json';
export const isIgnoredDirectory = (name: string): boolean => name.startsWith('.');
export const metadataFilename = 'metadata.json';

export const orgObjectFilename = 'org.json'; // for backup/restore

// IMPORTANT: When changing these values, also update ClientCacheName
export const customMusicDir = 'custom-music';
export const hymnalsDir = 'hymnals';
export const hymnalsPDFDir = 'hymnals';
export const vocalsDir = 'vocals';
export const songListsDir = 'song-lists';
export const clientDir = 'client';
export const distDir = 'dist';
export const releaseDir = 'release';
export const billingDir = 'billing';

export const orgHymnalDefaultIconPath = '/images/open-book.svg';

export const hymnalsManifestURL = `/${hymnalsDir}/${musicManifestFilename}`;

// The hash Vite puts in the bundle's script filename. The client reads it off its own document
// scripts and the server out of the served index.html, and the two are compared to notice a new
// build (GitHub issue #3265), so there is exactly one pattern. scripts/sypdev.mjs mirrors it.
export const bundleScriptPattern = /index-(?<bundleHash>[^.]+)\.js/;

export const requestQuoteUrl = 'https://www.surveymonkey.com/r/73RJWXY';

const CustomMusicHymnalName = 'cm';

export function getSongSlug({
  songNumber,
  hymnalSlug,
  orgId,
}: {
  songNumber: SongNumber;
  hymnalSlug?: HymnalSlug;
  orgId?: number;
}): SongSlug {
  if (!hymnalSlug && !orgId) {
    throw new Error(`Must specify either hymnalSlug or orgId for song number ${songNumber}`);
  }
  if (hymnalSlug === CustomMusicHymnalName && !orgId) {
    throw new Error(`Must specify orgId for custom song number ${songNumber}`);
  }
  // TODO(hewitt): Song slugs contain the word 'church' -> need to be renamed to 'org'... :(
  const prefix =
    hymnalSlug && hymnalSlug !== CustomMusicHymnalName ? `hymnal/${hymnalSlug.toLowerCase()}` : `church/${orgId}`;
  return `${prefix}/song/${songNumber}`;
}

export function encodeSongSlug(songSlug: SongSlug): string {
  const { songNumber, hymnalSlug, orgId } = parseSongSlug(songSlug);
  // note that we are note calling encodeMusicURI, which would have escaped '!' as '%21'
  // '!' is a valid URL character, so this is technically OK
  return getSongSlug({ songNumber: encodeURIComponent(songNumber), hymnalSlug, orgId });
}

export function decodeSongSlug(songSlug: SongSlug): SongSlug {
  const { songNumber, hymnalSlug, orgId } = parseSongSlug(songSlug);
  return getSongSlug({ songNumber: decodeURIComponent(songNumber), hymnalSlug, orgId });
}

export function getOrgCustomMusicManifestPathFromKey(
  organizationKey: OrganizationKey,
  { encodeURI }: { encodeURI?: boolean } = {},
): string {
  return `/${customMusicDir}/${encodeURI ? encodeMusicURI(organizationKey) : organizationKey}/${musicManifestFilename}`;
}

export function getHymnalManifestUrl(hymnalName: string) {
  return `/${hymnalsDir}/${encodeMusicURI(hymnalName)}/${musicManifestFilename}`;
}

export function getHymnalIconPath(hymnalName: string) {
  return `/${hymnalsDir}/${hymnalName}/icon.png`;
}

// An alternate title's icon lives in the canonical hymnal's directory (issue #3454)
export function getHymnalAlternateTitleIconPath(canonicalHymnalName: string, iconFilename: string | undefined) {
  return iconFilename
    ? `/${hymnalsDir}/${canonicalHymnalName}/${iconFilename}`
    : getHymnalIconPath(canonicalHymnalName);
}

const validSongNumberPattern = /^[a-zA-Z0-9][a-zA-Z0-9.\-()]*$/;

export function isValidSongNumber(songNumber: string): boolean {
  return validSongNumberPattern.test(songNumber);
}

export function parseSongSlug(slug: SongSlug): {
  songNumber: SongNumber;
  hymnalSlug?: HymnalSlug;
  orgId?: number;
} {
  const { prefix, hymnalSlugOrOrgId, songNumber } =
    slug.match(/^(?<prefix>(hymnal|church))\/(?<hymnalSlugOrOrgId>[a-zA-Z0-9]+)\/song\/(?<songNumber>[^ /]+)$/)
      ?.groups ?? {};
  if (!prefix || !hymnalSlugOrOrgId) {
    throw new Error(`Malformed song slug: ${slug}`);
  }
  if (!songNumber || !isValidSongNumber(songNumber)) {
    throw new Error(`Invalid song number in slug: ${slug}`);
  }
  if (prefix === 'hymnal') {
    return { songNumber, hymnalSlug: hymnalSlugOrOrgId as HymnalSlug };
  } else {
    const orgId = Number(hymnalSlugOrOrgId);
    if (isNaN(orgId)) {
      throw new Error(`Malformed song slug: ${slug}`);
    }
    return { songNumber, orgId };
  }
}

// the org whose custom song the slug names (church/<orgId>/song/<number>); undefined for a hymnal song or a
// malformed slug
export function customSongOrgId(slug: string): OrgId | undefined {
  try {
    return parseSongSlug(slug).orgId;
  } catch {
    return undefined;
  }
}

export function isSongSlug(text: string): text is SongSlug {
  try {
    const { songNumber, hymnalSlug, orgId } = parseSongSlug(text);
    return songNumber !== undefined && (hymnalSlug !== undefined || orgId !== undefined); // arguably unnecessary check
  } catch {
    return false;
  }
}

export function getOrgDirectoryName(org: Org): string {
  return `${org.id} - ${org.name} (${org.location})`;
}

export const PrimaryOrgHymnalName = 'Our Hymnal';
export const overridesFilename = 'overrides.json';
export const tempoOverridesFilename = '_tempo_overrides.json';
export const copyrightOverridesFilename = '_copyright_overrides.json';

export function encodeMusicURI(uri: string) {
  return encodeURIComponent(uri).replace(/!/g, '%21');
}

export function getSongNumberFromFilename(filename: string): SongNumber | undefined {
  return filename.match(/^(?<songNumber>\S+)/)?.groups?.songNumber;
}
